<template>
  <div>
    <PageAllow controller="AccountUsers" action="index" />
    <Breadcrumbs :items="items" />

    <v-slide-x-transition>
      <v-sheet
        v-show="!currentItemMode"
        class="transparent-background-color fill-content-height"
      >
        <v-row dense class="mx-5">
          <v-col cols="12" sm="8" md="4">
            <v-row dense align="center" class="pa-1">
              <SearcherKeyword
                ref="name"
                v-model="options.keyword"
                @text="keyword = $event"
                :label="$t('AccountUser.keyword')"
              />
            </v-row>
          </v-col>
          <v-col cols="12" sm="12" md="8" align-self="center">
            <v-row dense align="center" class="search-controls">
              <v-tooltip bottom>
                <template v-slot:activator="{ on, attrs }">
                  <v-btn
                    v-bind="attrs"
                    v-on="on"
                    small
                    text
                    class="px-0 py-0 caption font-weight-bold grey--text"
                    color="primary"
                    @click.stop="onSearch()"
                  >
                    <v-icon small class="pe-1">fa-search</v-icon>
                    {{ $t("Search.search") }}
                  </v-btn>
                </template>
                <span>{{ $t("Search.searchTip") }}</span>
              </v-tooltip>
              <v-tooltip bottom>
                <template v-slot:activator="{ on, attrs }">
                  <v-btn
                    v-bind="attrs"
                    v-on="on"
                    small
                    text
                    class="caption font-weight-bold grey--text"
                    @click.stop="resetSearch()"
                  >
                    <v-icon x-small class="pe-1">fa-trash-alt</v-icon>
                    {{ $t("Search.reset") }}
                  </v-btn>
                </template>
                <span>{{ $t("Search.resetTip") }}</span>
              </v-tooltip>
              <v-divider vertical />
              <v-btn
                class="caption font-weight-bold grey--text"
                :class="
                  $vuetify.theme.dark ? 'text--lighten-1' : 'text--darken-1'
                "
                small
                text
                @click.stop="$store.commit('toggleSearchPanel')"
              >
                {{ $t("Search.pro") }}
                <v-icon small class="ps-1">{{
                    $store.state.showSearchPanel ? "fa-angle-up" : "fa-angle-down"
                  }}</v-icon>
              </v-btn>
              <v-spacer />
              <v-btn
                small
                color="primary"
                class="ms-1"
                @click.stop="
                  currentItemMode = 'new';
                  currentItem = { id: 0 };
                "
              >
                <v-icon x-small class="pe-1">fa-plus</v-icon>
                {{ $t("CRUD.newInfo", [$t("AccountUser.ObjectName")]) }}
              </v-btn>
            </v-row>
          </v-col>
        </v-row>
        <v-expansion-panels
          v-model="$store.getters.panels"
          multiple
          flat
          accordion
        >
          <v-expansion-panel class="transparent-background-color">
            <v-expansion-panel-content>
              <v-row dense align="center">
                <v-col cols="12" sm="6" md="4">
                  <SearcherRoles v-model="rolesSelected" />
                </v-col>
              </v-row>
            </v-expansion-panel-content>
          </v-expansion-panel>
        </v-expansion-panels>

        <!-- 表格 -->
        <div class="mx-4 mb-4">
          <v-data-table
            :headers="headers"
            :items="accountUsers"
            :options.sync="options"
            :loading="loading"
            :page.sync="paging.page"
            :server-items-length="paging.count"
            multi-sort
            class="elevation-2 px-4 pt-1"
            :footer-props="$t('$config.footer-props')"
          >
            <template v-slot:header.actions="{ header }">
              {{ header.text }}
              <v-icon x-small>fa-sliders-h</v-icon>
            </template>
            <template v-slot:item.sex="{ item }">
              {{ item.sex | sexFlgsText }}
            </template>
            <template v-slot:item.actions="{ item }">
              <v-tooltip top>
                <template v-slot:activator="{ on, attrs }">
                  <v-icon
                    small
                    v-bind="attrs"
                    v-on="on"
                    color="secondary"
                    class="px-1 py-4 me-1"
                    @click.stop="
                      currentItemMode = 'view';
                      currentItem = item;
                    "
                    >fa-file-alt
                  </v-icon>
                </template>
                <span>
                  {{ $t("CRUD.viewInfo", [$t("AccountUser.ObjectName")]) }}
                </span>
              </v-tooltip>
              <v-tooltip top>
                <template v-slot:activator="{ on, attrs }">
                  <v-icon
                    small
                    v-bind="attrs"
                    v-on="on"
                    color="primary"
                    class="px-1 py-4 me-1"
                    @click.stop="
                      currentItemMode = 'edit';
                      currentItem = item;
                    "
                    >fa-pencil-alt
                  </v-icon>
                </template>
                <span>
                  {{ $t("CRUD.editInfo", [$t("AccountUser.ObjectName")]) }}
                </span>
              </v-tooltip>
              <v-tooltip top>
                <template v-slot:activator="{ on, attrs }">
                  <v-icon
                    small
                    v-bind="attrs"
                    v-on="on"
                    color="danger"
                    class="px-1 me-1"
                    @click.stop="
                      currentItem = item;
                      deleteDialog = true;
                    "
                    >fa-trash-alt
                  </v-icon>
                </template>
                <span>
                  {{ $t("CRUD.deleteInfo", [$t("AccountUser.ObjectName")]) }}
                </span>
              </v-tooltip>
            </template>
          </v-data-table>
        </div>
      </v-sheet>
    </v-slide-x-transition>

    <AccountUserDetail
      v-show="currentItemMode"
      v-model="currentItem"
      :mode="currentItemMode"
      @mode="currentItemMode = $event"
      @save="getAccountUsersFromAPI()"
    />
    <DialogDelete
      v-model="deleteDialog"
      :label="$t('AccountUser.ObjectName')"
      @confirm="
        deleteAccountUser();
        deleteDialog = false;
      "
    />
  </div>
</template>
<script>
import { mapState } from "vuex";
import i18n from "@/i18n";
import PageAllow from "@/components/PageAllow";
import Breadcrumbs from "@/components/Breadcrumbs";
import SearcherKeyword from "@/components/plugins/SearcherKeyword";
import AccountUserDetail from "./plugins/Detail";
import DialogDelete from "@/components/plugins/DialogDelete";
import AccountUsers from "@/api/account_users";
import SearcherRoles from "@/components/plugins/SearcherRoles.vue";

const defaultOptions = {
  type: 2,
  keyword: null,
  role_id: []
};

export default {
  components: {
    SearcherRoles,
    PageAllow,
    Breadcrumbs,
    AccountUserDetail,
    DialogDelete,
    SearcherKeyword,
  },

  computed: {
    items: () => [
      {
        text: i18n.t("SettingNav.home"),
        disabled: false,
        href: "/home",
      },
      {
        text: i18n.t("SettingNav.settings"),
        disabled: false,
        href: "/settings/bussiness-settings",
      },
      {
        text: i18n.t("SettingNav.accountUsers"),
        disabled: true,
        href: "/settings/account-users",
      },
    ],

    headers: () => [
      {
        text: i18n.t("AccountUser.Fields.organization"),
        align: "start",
        sortable: false,
        value: "organization.name",
      },
      {
        text: i18n.t("AccountUser.Fields.role"),
        align: "center",
        sortable: false,
        value: "role.role_name",
      },
      {
        text: i18n.t("AccountUser.Fields.name"),
        align: "start",
        sortable: false,
        value: "name",
      },
      {
        text: i18n.t("AccountUser.Fields.number"),
        align: "center",
        sortable: false,
        value: "job_number",
      },
      {
        text: i18n.t("AccountUser.Fields.sex"),
        align: "center",
        sortable: false,
        value: "sex",
      },
      {
        text: i18n.t("AccountUser.Fields.account_user_id"),
        align: "center",
        sortable: false,
        value: "account_user_id",
      },
      {
        text: i18n.t("TableHeaders.actions"),
        align: "start",
        sortable: false,
        value: "actions",
      },
    ],

    ...mapState(["loading", "settings"]),
  },

  data: () => ({
    options: JSON.parse(
      sessionStorage.getItem("account_user-options")
      ? sessionStorage.getItem("account_user-options")
      : JSON.stringify(defaultOptions)
    ),
    keyword: null,
    rolesSelected: [],
    paging: {},
    accountUsers: [],
    deleteDialog: false,
    currentItem: null,
    currentItemMode: null,
  }),

  mounted() {
    this.$commonUtils
        .loadOptions("account_user", [
          "rolesSelected",
        ])
        .then(
            function (data) {
              if (data.options) {
                this.options = data.options;
              } else this.resetSearch();

              data.selecteds.forEach((selected) => {
                switch (selected.key) {
                  case "rolesSelected":
                    this.rolesSelected = selected.value;
                    break;
                }
              });
            }.bind(this)
        );
  },

  methods: {
    resetSearch() {
      this.rolesSelected = [];
      this.options = JSON.parse(JSON.stringify(defaultOptions));
    },
    onSearch() {
      this.options = Object.assign({}, this.options, {
        keyword: this.keyword,
      });
    },
    getAccountUsersFromAPI() {
      AccountUsers.fetch(this.options).then(
        function (response) {
          if (response.data.status.isOK()) {
            this.accountUsers = response.data.account_users;
            this.paging = response.data.paging;
          } else if (response.data.error.code.isNoResultSet()) {
            this.options.page = 1;
          }
        }.bind(this)
      );
    },
    deleteAccountUser() {
      if (this.currentItem)
        AccountUsers.del(this.currentItem.id).then(
          function (response) {
            if (response.data.status.isOK()) {
              this.getAccountUsersFromAPI(); // 删除成功的话刷新数据
            }
          }.bind(this)
        );
    },
  },

  watch: {
    $route() {
      if (!this.$route.query.id) {
        this.currentItemMode = null;
        this.currentItem = null;
      }
    },
    currentItem: {
      handler() {
        if (this.currentItem) {
          this.$router.replace(
            "/settings/account-users?id=" + this.currentItem.id
          );
          this.$cacheScrollLocation();
        } else {
          this.$scrollToCachedLocation();
          this.$router.replace("/settings/account-users");
        }
      },
    },

    options: {
      deep: true,
      handler() {
        this.getAccountUsersFromAPI();
        sessionStorage.setItem(
            "account_user-options",
            JSON.stringify(this.options)
        );
      },
    },
    rolesSelected: {
      handler() {
        this.options.role_id = this.rolesSelected && this.rolesSelected.length
          ? this.rolesSelected.map((e) => {
            return e.id;})
          : [];
        sessionStorage.setItem(
            "account_user-rolesSelected",
            JSON.stringify(this.rolesSelected)
        );
      },
    },
  },
};
</script>